/* eslint-disable react-native/no-inline-styles */
import React, {useState, useCallback, useEffect, useRef} from 'react';
import {View, Linking, TouchableOpacity, StyleSheet} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useFetchActiveMasterySeries from '@/Hooks/SubscriptionApi/useFetchActiveMasterySeries';
import useGeneratePaymentApi from '@/Hooks/SubscriptionApi/useGeneratePayment';
import {useOrderStatusApi} from '@/Hooks/BooksApi/useBooksApi';
import useFetchTournamentEligibility from '@/Hooks/GetTournaments/useFetchTournamentEligibility';
import useFetchTournamentQuestions from '@/Hooks/GetTournaments/useFetchTournamentQuestions';
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
} from '@/Utils/CommonUtils';
import {useCouponStore} from '@/Store/couponStore';

import ProgressiveQuestions from './ProgressiveQuestions';
import EntryFeeSelect from './EntryFeeSelect';
import NotEligibleScreen from './NotEligibleScreen';
import SeriesRegistrationModal from './SeriesRegistrationModal';
import PaymentStatusModal from './PaymentStatusModal';
import {seriesStyles} from './styles/seriesStyles';
import {
  getPaymentStatus,
  isTerminalStatus,
  isStatusId,
  STATUS_IDS,
} from './utils/paymentStatus';
import {
  EligibilityCriterion,
  RegistrationStage,
} from './types/registrationTypes';
import Box from '@/Atoms/Box';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import dayjs from 'dayjs';

export interface PaidTournamentRegistrationModalProps {
  visible: boolean;
  tournament: any | null;
  userId: string;
  onClose: () => void;
  onRefreshDetails: () => void;
  orderId?: string | null;
  handleJoin: () => void;
}

const PaidTournamentRegistrationModal: React.FC<
  PaidTournamentRegistrationModalProps
> = ({
  visible,
  tournament,
  userId,
  onClose,
  onRefreshDetails,
  orderId,
  handleJoin,
}) => {
  const {isMobileOrTab} = useMediaQuery();
  const userData = useLoggedInUserStore(state => state.userData);
  const appliedCoupon = useCouponStore(s => s.appliedCoupon);

  // ── Stage state ─────────────────────────────────────────────────
  const [stage, setStage] = useState(0);
  const [, setStageReached] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedTournament, setSelectedTournament] = useState<any | null>(
    null,
  );
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const registrationInitDoneRef = useRef(false);
  const [pendingOrder, setPendingOrder] = useState<any | null>(null);
  const [continuePaymentUrl, setContinuePaymentUrl] = useState<string | null>(
    null,
  );
  const [eligibilityCriteria, setEligibilityCriteria] = useState<
    EligibilityCriterion[]
  >([]);
  const [membershipClubId, setMembershipClubId] = useState<number | null>(null);
  const [isMembershipPurchase, setIsMembershipPurchase] = useState(false);

  // ── Series pricing ───────────────────────────────────────────────
  const [masterySeries, setMasterySeries] = useState<any | null>(null);
  const [masteryCategoryAPrice, setMasteryCategoryAPrice] = useState<
    number | null
  >(null);
  const [masteryCategoryAMrp, setMasteryCategoryAMrp] = useState<number | null>(
    null,
  );
  const [masteryCategoryADiscount, setMasteryCategoryADiscount] = useState<
    number | null
  >(null);

  // ── Eligibility & questions ──────────────────────────────────────
  const {
    mutate: checkEligibility,
    data: eligibilityData,
    isLoading: eligibilityLoading,
  } = useFetchTournamentEligibility();
  const {
    mutate: getQuestions,
    data: questionsData,
    isLoading: questionLoading,
  } = useFetchTournamentQuestions();

  // ── Order status ─────────────────────────────────────────────────
  const {
    mutate: orderBookStatus,
    data: orderStatusResponse,
    isLoading: isStatusLoading,
  } = useOrderStatusApi();

  // ── Fetch mastery series ────────────────────────────────────────
  const {mutate: fetchMasterySeries, isLoading: masterySeriesLoading} =
    useFetchActiveMasterySeries(
      (data: any) => {
        const openings: any[] = data?.data?.openings ?? [];
        const matchingOpening = openings.find(
          opening => opening.id === tournament?.cct_id,
        );
        if (!matchingOpening) {
          setErrorMessage('Could not find series for this tournament.');
          return;
        }
        setMasterySeries(matchingOpening);
        const categoryAPlan = matchingOpening.plans?.find(
          (plan: any) => plan.is_per_tournament,
        );
        const pricing = categoryAPlan?.pricing;
        setMasteryCategoryAPrice(pricing?.discounted_price ?? null);
        setMasteryCategoryAMrp(pricing?.mrp ?? null);
        setMasteryCategoryADiscount(
          pricing?.applied_discount_percentage ?? null,
        );
      },
      () => {
        setErrorMessage('Failed to load series pricing. Please try again.');
      },
    );

  // ── Payment generation ───────────────────────────────────────────
  const {mutate: generatePayment} = useGeneratePaymentApi(
    async (paymentUrlData: any) => {
      if (paymentUrlData?.data?.content?.payment_links?.web) {
        const {
          order_id: generatedOrderId,
          payment_links: {web: paymentUrl},
          order_expiry: orderExpiry,
        } = paymentUrlData.data.content;

        const pendingData = {
          order_id: generatedOrderId,
          tournament_id: selectedTournament?.cct_id,
          tournament_registration_tid: selectedTournament?.registration_tid,
          payment_url: paymentUrl,
          order_expiry: orderExpiry,
          created_at: Date.now(),
        };

        await setLocalStorage(
          'pending_tournament_order',
          JSON.stringify(pendingData),
        );
        setPendingOrder(pendingData);
        setContinuePaymentUrl(paymentUrl);
        setStage(4);
        setIsPaymentProcessing(false);
        orderBookStatus({order_id: generatedOrderId});
        Linking.openURL(paymentUrl);
      } else if (paymentUrlData?.data?.success) {
        await removeLocalStorage('pending_tournament_order');
        onClose();
        setStage(0);
        onRefreshDetails();
      }
    },
    (error: any) => {
      console.log('Error generating payment:', error);
      setErrorMessage('Failed to generate payment. Please try again.');
      setIsPaymentProcessing(false);
    },
  );

  // ── Eligibility response → questions or entry fee ───────────────
  useEffect(() => {
    if (!eligibilityData) {
      return;
    }

    console.log('Eligibility data:', eligibilityData);

    if (eligibilityData?.eligibility_criteria) {
      setEligibilityCriteria(eligibilityData.eligibility_criteria);
    }

    // Check if player is not eligible due to missing membership
    const isNotEligibleDueToMembership =
      eligibilityData?.status === false &&
      (eligibilityData?.error?.toLowerCase()?.includes('membership') ||
        eligibilityData?.error?.toLowerCase()?.includes('not a member') ||
        eligibilityData?.error?.toLowerCase()?.includes('not a member'));

    if (eligibilityData?.status) {
      getQuestions({
        tournament_id: String(selectedTournament?.registration_tid),
        user_id: Number(userData?.player),
      });
      setStage(RegistrationStage.QUESTIONS);
      setStageReached(true);
    } else if (isNotEligibleDueToMembership) {
      // Player doesn't have membership - show NOT ELIGIBLE screen first
      // User can then choose to buy membership if they want
      setMembershipClubId(eligibilityData?.club_id || null);
      setErrorMessage(
        eligibilityData?.error || 'You are not eligible for this tournament.',
      );
      setStage(RegistrationStage.NOT_ELIGIBLE);
      setStageReached(true);
    } else if (eligibilityData?.club_id) {
      setErrorMessage(eligibilityData?.error);
      if (selectedTournament?.club) {
        setSelectedTournament({
          ...selectedTournament.club,
          registration_tid: eligibilityData?.club_id,
        });
      }
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: Number(eligibilityData?.club_id),
      });
    } else if (eligibilityData?.status === false) {
      setErrorMessage(
        eligibilityData?.error || 'You are not eligible for this tournament.',
      );
      setStage(RegistrationStage.NOT_ELIGIBLE);
      setStageReached(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityData]);

  // ── Skip questions stage if no questions ─────────────────────────
  useEffect(() => {
    if (questionsData && !questionsData?.questions?.length) {
      setStage(2);
      setStageReached(true);
    }
  }, [questionsData]);

  // ── Track stage progression ──────────────────────────────────────
  useEffect(() => {
    if (stage > 0) {
      setStageReached(true);
    }
  }, [stage]);

  // ── Payment success → join ───────────────────────────────────────
  useEffect(() => {
    if (orderStatusResponse?.status_id === 21 && selectedTournament) {
      handleJoin();
    }
  }, [
    orderStatusResponse?.status_id,
    selectedTournament,
    userData?.player,
    handleJoin,
  ]);

  // ── Poll order status every 30s ─────────────────────────────────
  useEffect(() => {
    if (
      !pendingOrder?.order_id ||
      isTerminalStatus(orderStatusResponse?.status_id)
    ) {
      return;
    }
    const intervalId = setInterval(() => {
      orderBookStatus({order_id: pendingOrder.order_id});
    }, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOrder?.order_id, orderStatusResponse?.status_id]);

  // ── Order status response → manage pending order lifecycle ───────
  useEffect(() => {
    if (!orderStatusResponse || !pendingOrder) {
      return;
    }

    const {status_id: statusId, order_expiry: expiry} = orderStatusResponse;

    if (statusId === 21) {
      removeLocalStorage('pending_tournament_order');
      setPendingOrder(null);
      setContinuePaymentUrl(null);
      onRefreshDetails();
      return;
    }

    if (
      isStatusId(statusId, STATUS_IDS.FAILED) ||
      (expiry && new Date(expiry).getTime() < Date.now())
    ) {
      removeLocalStorage('pending_tournament_order');
      setPendingOrder(null);
      setContinuePaymentUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStatusResponse, pendingOrder]);

  // ── Init on modal open ───────────────────────────────────────────
  useEffect(() => {
    if (!visible) {
      registrationInitDoneRef.current = false;
      setStageReached(false);
      return;
    }
    if (!tournament || orderId) {
      return;
    }
    if (registrationInitDoneRef.current) {
      return;
    }

    registrationInitDoneRef.current = true;
    setErrorMessage('');
    setSelectedTournament(tournament);

    if (tournament.is_series) {
      setStage(3);
      fetchMasterySeries({user_id: userId, currency: 'INR'});
    } else {
      setStage(0);
      checkEligibility({
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        registration_tid: tournament?.registration_tid,
      });
    }
  }, [
    visible,
    tournament?.id,
    orderId,
    tournament,
    fetchMasterySeries,
    userId,
    checkEligibility,
    userData?.mobile_number,
    userData?.player,
  ]);

  // ── Restore pending order on mount ──────────────────────────────
  useEffect(() => {
    if (!visible || !tournament) {
      return;
    }

    const checkAndSetPendingOrder = async () => {
      const pending = (await getLocalStorage(
        'pending_tournament_order',
      )) as string;
      if (!pending) {
        return;
      }

      const pendingData = JSON.parse(pending);
      if (
        pendingData?.tournament_registration_tid !==
        tournament?.registration_tid
      ) {
        return;
      }

      if (
        pendingData?.order_expiry &&
        new Date(pendingData.order_expiry).getTime() < Date.now()
      ) {
        await removeLocalStorage('pending_tournament_order');
        return;
      }

      setSelectedTournament(tournament);
      orderBookStatus({order_id: pendingData.order_id});
      setPendingOrder(pendingData);
      setContinuePaymentUrl(pendingData.payment_url);
      setStage(4);
      setStageReached(true);
    };

    checkAndSetPendingOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, tournament]);

  // ── Legacy polling for orderId route param ───────────────────────
  useEffect(() => {
    if (stage !== 4 || !orderId) {
      return;
    }
    if (isTerminalStatus(orderStatusResponse?.status_id)) {
      return;
    }
    if (pollCount >= 10) {
      return;
    }

    const timer = setTimeout(() => {
      orderBookStatus({order_id: orderId});
      setPollCount(prev => prev + 1);
    }, 3000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, orderId, orderStatusResponse?.status_id, pollCount]);

  // ── Handle orderId from route params ────────────────────────────
  useEffect(() => {
    if (!orderId || !tournament) {
      return;
    }
    setSelectedTournament(tournament);
    setStage(4);
    orderBookStatus({order_id: orderId});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, tournament]);

  // ── Payment handlers ─────────────────────────────────────────────
  const handleSeriesPayment = useCallback(async () => {
    await removeLocalStorage('pending_tournament_order');
    setPendingOrder(null);
    setContinuePaymentUrl(null);
    setIsPaymentProcessing(true);

    const categoryPlan = masterySeries?.plans?.find(
      (p: any) => p.is_per_tournament,
    );
    const categoryName = categoryPlan?.category_name;
    const currency = categoryPlan?.pricing?.currency || 'INR';

    generatePayment({
      mobile_number: userData?.mobile_number,
      customer_id: userData?.user_id,
      email: userData?.email,
      return_url: typeof window !== 'undefined' ? window.location?.href : '',
      cart_items: [
        {
          tournament_id: masterySeries?.id,
          player_id: userData?.player,
          category_str: `${currency} / ${categoryName}`,
          selected_tournament_ids: [selectedTournament?.id],
        },
      ],
      ...(appliedCoupon ? {coupon_code: appliedCoupon.code} : {}),
    });
  }, [
    masterySeries,
    selectedTournament,
    userData,
    appliedCoupon,
    generatePayment,
  ]);

  const handlePayment = useCallback(
    async (category: string, amount: number) => {
      await removeLocalStorage('pending_tournament_order');
      setPendingOrder(null);
      setContinuePaymentUrl(null);
      setIsPaymentProcessing(true);

      generatePayment({
        category_str: `INR ${amount} / ${category}`,
        customer_id: userData?.user_id,
        email: userData?.email,
        mobile_number: userData?.mobile_number,
        player_id: userData?.player,
        tournament_id: selectedTournament?.cct_id,
        amount,
        return_url: typeof window !== 'undefined' ? window.location?.href : '',
        ...(appliedCoupon ? {coupon_code: appliedCoupon.code} : {}),
      });
    },
    [selectedTournament, userData, appliedCoupon, generatePayment],
  );

  const handleClose = useCallback(async () => {
    setStage(0);
    setStageReached(false);
    setErrorMessage('');
    setIsPaymentProcessing(false);
    setPollCount(0);
    setPendingOrder(null);
    setContinuePaymentUrl(null);
    setMasterySeries(null);
    setMasteryCategoryAPrice(null);
    setMasteryCategoryAMrp(null);
    setMasteryCategoryADiscount(null);
    await removeLocalStorage('pending_tournament_order');
    onClose();
  }, [onClose]);

  const paymentStatus = getPaymentStatus(orderStatusResponse?.status_id);
  const isLoading = eligibilityLoading || questionLoading || isStatusLoading;

  // ── Modal header config ──────────────────────────────────────────
  const headerConfig = (() => {
    if (isMembershipPurchase && stage === RegistrationStage.ENTRY_FEE) {
      return {eyebrow: 'Membership Purchase', title: 'Select Membership Plan'};
    }
    switch (stage) {
      case RegistrationStage.PAYMENT_STATUS:
        return {
          eyebrow: 'Tournament Registration · Step 3 of 3',
          title: 'Payment Status',
        };
      case RegistrationStage.NOT_ELIGIBLE:
        return {
          eyebrow: 'Eligibility Check',
          title: "You're not eligible for this one",
        };
      case RegistrationStage.QUESTIONS:
        return {
          eyebrow: 'Tournament Registration · Step 1 of 3',
          title: 'A few questions before you play',
        };
      default:
        return {
          eyebrow: 'Tournament Registration · Step 2 of 3',
          title: 'Select Entry Fee',
        };
    }
  })();

  const isSeriesStage = stage === 3;
  const showCustomHeader = !isSeriesStage;

  return (
    <SlideModal
      type={isMobileOrTab ? 'bottom' : 'center'}
      visible={visible}
      modalHeaderTitle=""
      onClose={handleClose}
      showHeader={false}
      contentPadding={0}
      customBodyStyle={{
        padding: 0,
      }}
      minWidth={!isMobileOrTab ? (isSeriesStage ? 720 : 500) : undefined}
      customMobileStyle={
        isSeriesStage && isMobileOrTab ? ({height: 'auto'} as any) : undefined
      }
      data-testid="paid-registration-modal">
      {isLoading ? (
        <View style={seriesStyles.loadingWrapper} data-testid="modal-loading">
          <CCLoader loading bgWhite isComponentLoader />
        </View>
      ) : isSeriesStage ? (
        <SeriesRegistrationModal
          tournament={selectedTournament}
          masterySeries={masterySeries}
          isLoading={masterySeriesLoading}
          categoryAPrice={masteryCategoryAPrice}
          mrp={masteryCategoryAMrp}
          discountPercentage={masteryCategoryADiscount}
          isPaymentProcessing={isPaymentProcessing}
          errorMessage={errorMessage}
          onPayNow={handleSeriesPayment}
          onClose={handleClose}
          couponExtraParams={{tournament_id: selectedTournament?.cct_id}}
        />
      ) : (
        <View data-testid="modal-content">
          {/* Custom gradient header */}
          {showCustomHeader && (
            <View
              style={[
                modalHeaderStyles.header,
                // @ts-ignore — web-only CSS gradient
                {
                  background:
                    'linear-gradient(180deg, #fff7d5 0%, #FFFFFF 100%)',
                },
              ]}>
              <View style={modalHeaderStyles.headerContent}>
                <View style={{flex: 1}}>
                  <CCText
                    fontSize={11}
                    fontWeight="bold"
                    color="#888280"
                    style={modalHeaderStyles.eyebrow}>
                    {headerConfig.eyebrow}
                  </CCText>
                  <CCText
                    fontSize={28}
                    fontWeight="thunder"
                    color="#262322"
                    style={modalHeaderStyles.title}>
                    {headerConfig.title}
                  </CCText>
                </View>
                <TouchableOpacity
                  onPress={handleClose}
                  style={modalHeaderStyles.closeBtn}
                  activeOpacity={0.7}>
                  <Ionicons name="close-outline" size={22} color="#4D3F37" />
                </TouchableOpacity>
              </View>
              {/* Tournament chip */}
              {tournament && (
                <View style={modalHeaderStyles.tch}>
                  <View style={modalHeaderStyles.tchIcon}>
                    <MaterialCommunityIcons
                      name="chess-knight"
                      size={18}
                      color="#FACF47"
                    />
                  </View>
                  <View style={modalHeaderStyles.tchText}>
                    <CCText
                      fontSize={13}
                      fontWeight="semibold"
                      color="#262322"
                      showEllipses>
                      {isMembershipPurchase
                        ? tournament.mandatory_club_membership_name
                        : tournament.name}
                    </CCText>
                    {!isMembershipPurchase && (
                      <Box
                        type="row-flex-start"
                        alignItems="center"
                        gap={12}
                        marginTop={4}>
                        <Box type="row-flex-start" alignItems="center" gap={4}>
                          <MaterialCommunityIcons
                            name="calendar-range"
                            size={12}
                            color="#888280"
                          />
                          <CCText fontSize={11} color="#888280">
                            {dayjs(tournament.start_date).format(
                              'd MMM YYYY h:mm A',
                            )}
                          </CCText>
                        </Box>
                        <Box type="row-flex-start" alignItems="center" gap={4}>
                          <MaterialCommunityIcons
                            name="clock-outline"
                            size={12}
                            color="#888280"
                          />
                          <CCText fontSize={11} color="#888280">
                            {[tournament.time_format, tournament.time_control]
                              .filter(Boolean)
                              .join(' · ')}
                          </CCText>
                        </Box>
                      </Box>
                    )}
                  </View>
                </View>
              )}
            </View>
          )}
          <View
            style={[
              seriesStyles.standardModalBody,
              {paddingTop: showCustomHeader ? 0 : 22},
              isMobileOrTab && {padding: 16},
            ]}>
            {stage === 4 && (
              <PaymentStatusModal
                status={paymentStatus}
                onClose={handleClose}
                pendingOrder={pendingOrder}
                onRetry={() => {
                  if (continuePaymentUrl) {
                    Linking.openURL(continuePaymentUrl);
                    setContinuePaymentUrl(null);
                  }
                }}
                isProcessing={isPaymentProcessing}
              />
            )}
            {stage === RegistrationStage.NOT_ELIGIBLE && (
              <NotEligibleScreen
                criteria={eligibilityCriteria}
                errorMessage={errorMessage}
                onClose={handleClose}
                onSeeAllTournaments={() => navigation.navigate('CCTournaments')}
                onBuyMembership={() => {
                  if (membershipClubId) {
                    setIsMembershipPurchase(true);
                    setStage(RegistrationStage.ENTRY_FEE);
                    checkEligibility({
                      mobile_number: userData?.mobile_number,
                      player_id: userData?.player,
                      registration_tid: membershipClubId,
                    });
                  }
                }}
              />
            )}
            {stage === RegistrationStage.ENTRY_FEE && (
              <EntryFeeSelect
                errorMessage={isMembershipPurchase ? '' : errorMessage}
                tournament={selectedTournament}
                categories={eligibilityData?.category_fees || {}}
                discountDetails={eligibilityData?.discount_details}
                handlePayment={handlePayment}
                isPaymentProcessing={isPaymentProcessing}
                continuePaymentUrl={continuePaymentUrl}
                pendingOrder={pendingOrder}
                setContinuePaymentUrl={setContinuePaymentUrl}
                couponExtraParams={{tournament_id: selectedTournament?.cct_id}}
                isMembershipPurchase={isMembershipPurchase}
              />
            )}
            {stage === RegistrationStage.QUESTIONS && (
              <ProgressiveQuestions
                questions={questionsData?.questions || []}
                userData={userData}
                selectedTournament={selectedTournament}
                onComplete={() => setStage(RegistrationStage.ENTRY_FEE)}
              />
            )}
          </View>
        </View>
      )}
    </SlideModal>
  );
};

const modalHeaderStyles = StyleSheet.create({
  /* ── Tournament chip ── */
  tch: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 10,
    marginBottom: 16,
    paddingVertical: 10,
    paddingLeft: 10,
    paddingRight: 14,
    backgroundColor: '#FAF6EB',
    borderWidth: 1,
    borderColor: 'rgba(77, 63, 55, 0.08)',
    borderRadius: 14,
    maxWidth: 'fit-content',
  },
  tchIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    backgroundColor: '#4D3F37',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tchText: {
    flex: 1,
  },
  header: {
    paddingTop: 22,
    paddingBottom: 10,
    paddingHorizontal: 26,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  eyebrow: {
    letterSpacing: 0.88,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  title: {
    lineHeight: 34,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(77, 63, 55, 0.06)',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 12,
    marginTop: 2,
  },
});

export default PaidTournamentRegistrationModal;
